/**
 * QueryBridge — wires SessionRunner lifecycle state to QueryExecutor execution.
 *
 * SessionRunner handles connect/idle/message lifecycle.
 * QueryExecutor handles Claude SDK query orchestration.
 * QueryBridge adapts between them by constructing the QueryHost interface
 * that QueryExecutor expects, using live references to ModeController state.
 */

import type { HarnessEvent, HarnessUserMessage, PtyBridge } from "../harness/index.js";
import { createHarness, type HarnessKind } from "../harness/index.js";
import {
  resolveTuiAdapter,
  resolveTuiKindFromEnv,
  type TuiKind,
} from "../harness/pty/adapters/index.js";
import { wrapBridgeWithDirectStream, type DirectPtyStream } from "../harness/pty/direct-stream.js";
import {
  runSdkQuery,
  runPassiveTurn,
  resolvePromptDelivery,
  hasExistingSessionFile,
  type QueryHost,
} from "../execution/query-executor.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import type { ModeController } from "./mode-controller.js";
import type { AgentRunnerConfig, AgentRunnerCallbacks } from "../runner-types.js";
import type { TaskContext, AgentMode, MultimodalBlock, RunnerMode } from "@project/shared";
import { createServiceLogger } from "../utils/logger.js";

const logger = createServiceLogger("QueryBridge");

/**
 * Pick the harness implementation for the task chat path.
 *
 * Card/task chat is ALWAYS the PTY harness (drives the `claude` CLI under a
 * pseudo-terminal, rendered as the Connected TUI). This is unconditional: the
 * env var `CONVEYOR_HARNESS` is deliberately NOT consulted, so a stale value
 * (a leftover codespace secret, or one applied via the bootstrap envVars) can
 * never route a card to the headless SDK loop — the failure mode that left
 * auto-mode cards running SDK with no Connected-TUI tab.
 *
 * The ONLY way to put a card on the SDK harness is an explicit maintainer
 * override, `CONVEYOR_FORCE_SDK_CARDS=1`. It is a distinct name that is never
 * pushed as a codespace secret nor emitted in the bootstrap env, so it cannot
 * be triggered accidentally — it exists solely for emergency local debugging.
 *
 * Scope: this governs ONLY the task chat. The audit and project-chat paths
 * construct the SDK harness directly via `createHarness()` (no-arg) and never
 * call this function, so they stay on SDK regardless.
 */
export function resolveHarnessKind(): HarnessKind {
  if (process.env.CONVEYOR_FORCE_SDK_CARDS === "1") return "sdk";
  // Cards are unconditionally PTY — for opencode too. The opencode adapter
  // gained a trusted structured-event source (the Conveyor events plugin
  // tailing the client engine's bus — see harness/opencode/plugin.ts), so an
  // opencode card mirrors chat, mounts the Conveyor MCP tools, opens PRs and
  // ends turns WHILE the human watches and types in the live Connected-TUI
  // tab. The headless `opencode run` harness (`HarnessKind "opencode"`) is
  // kept in-tree but unrouted, as the fallback if the TUI path hits pod
  // trouble.
  return "pty";
}

/**
 * Which TUI a card's PTY harness drives.
 *
 * The card's `Task.codingAgentTui` reaches the pod as `CONVEYOR_TUI` (via
 * `key-resolution.ts` → the bootstrap bundle), alongside that TUI's
 * credentials. Before this existed the card path hard-wired Claude, so an
 * opencode card booted with opencode credentials and then spawned the `claude`
 * binary — parking at a sign-in prompt it had no key for.
 *
 * `code-review` is pinned to Claude regardless: the reviewer's verdict comes
 * from the MCP tools (`approve_code_review` / `request_code_changes`), which
 * only mount on an adapter with structured events. A raw-relay reviewer would
 * run, produce no verdict, and park until the stale sweep auto-approved it.
 */
export function resolveCardTui(mode: RunnerMode | undefined): TuiKind {
  if (mode === "code-review") return "claude-code";
  return resolveTuiKindFromEnv(process.env);
}

/**
 * Adapt an AgentConnection into the harness-neutral PtyBridge consumed by the
 * PTY harness. Keeps the harness package free of any connection-layer import.
 */
function buildPtyBridge(connection: AgentConnection, onBackgroundTaskDone: () => void): PtyBridge {
  return {
    sendOutput: (data, dims) => connection.sendPtyOutput(data, dims),
    sendChatEvent: (event) => connection.sendPtyChatEvent(event),
    sendEnded: () => connection.sendPtyEnded(),
    notifyBackgroundTaskDone: onBackgroundTaskDone,
    notifyAuthNotReady: (detail) => connection.postChatMessage(detail),
    notifyPromptDeliveryFailure: (detail) => connection.postChatMessage(detail),
    requestPodRecycle: (reason) => connection.requestWorkspaceRecycle(reason),
    onInput: (handler) => connection.onPtyInput(handler),
    onResize: (handler) => connection.onPtyResize(handler),
  };
}

export class QueryBridge {
  private readonly harness;
  /** In-pod PTY stream server + its bridge decorator (PTY harness only). */
  private readonly directStream: DirectPtyStream | null = null;
  /** Which harness drives this bridge ("pty" task chat; "sdk" only under the
   *  CONVEYOR_FORCE_SDK_CARDS maintainer override). */
  readonly harnessKind: HarnessKind;
  private readonly sessionIds = new Map<string, string>();
  private activeQuery: AsyncGenerator<HarnessEvent, void> | null = null;
  private readonly pendingToolOutputs: { tool: string; output: string }[] = [];
  private _stopped = false;
  private _discoveryCompleted = false;
  private _isParentTask = false;
  private _wasRateLimited = false;
  private _wedgeAborted = false;
  private _keyCycleCount = 0;
  private _abortController: AbortController | null = null;

  /** Called by SessionRunner when ExitPlanMode triggers a mode transition. */
  onModeTransition?: (newMode: AgentMode) => void;

  /** Called by tool handlers to soft-stop (abort query, keep session alive). */
  onSoftStop?: () => void;

  /** Called when the CLI injects a background-task completion notification —
   *  one `run_in_background` command / backgrounded subagent finished. Set by
   *  SessionRunner (post-construction, like onSoftStop) and read lazily, so the
   *  bridge built in the constructor can already close over it. */
  onBackgroundTaskDone?: () => void;

  constructor(
    private readonly connection: AgentConnection,
    private readonly mode: ModeController,
    private readonly runnerConfig: AgentRunnerConfig,
    private readonly callbacks: AgentRunnerCallbacks,
  ) {
    const harnessKind = resolveHarnessKind();
    this.harnessKind = harnessKind;
    let bridge: PtyBridge | undefined;
    if (harnessKind === "pty") {
      // The relay bridge is wrapped, never replaced: viewers that can reach the
      // pod stream from it directly, and anything that can't keeps working over
      // the `pty:*` relay underneath.
      this.directStream = wrapBridgeWithDirectStream(
        buildPtyBridge(connection, () => this.onBackgroundTaskDone?.()),
        connection,
      );
      bridge = this.directStream.bridge;
    }
    this.harness = createHarness(
      harnessKind,
      bridge,
      harnessKind === "pty" ? resolveTuiAdapter(resolveCardTui(runnerConfig.mode)) : undefined,
    );
  }

  get isStopped(): boolean {
    return this._stopped;
  }

  get isDiscoveryCompleted(): boolean {
    return this._discoveryCompleted;
  }

  set isDiscoveryCompleted(val: boolean) {
    this._discoveryCompleted = val;
  }

  get isParentTask(): boolean {
    return this._isParentTask;
  }

  set isParentTask(val: boolean) {
    this._isParentTask = val;
  }

  get wasRateLimited(): boolean {
    return this._wasRateLimited;
  }

  /**
   * Whether the turn that just ran was aborted by the mid-turn wedge watchdog
   * (30 minutes of complete event silence). Reset at the start of every
   * execute(); SessionRunner reads it to decide whether an autonomous initial
   * query should self-requeue.
   */
  get lastTurnWedgeAborted(): boolean {
    return this._wedgeAborted;
  }

  /**
   * Ask the live terminal (PTY harness only) to redraw its full screen.
   * Safe no-op on the SDK harness or between queries.
   */
  forceRepaint(): void {
    this.harness.forceRepaint?.();
  }

  stop(): void {
    this._stopped = true;
    this._abortController?.abort();
  }

  resume(): void {
    this._stopped = false;
  }

  /**
   * Inject a follow-up message into the turn currently running under the harness
   * (PTY keep-alive) instead of aborting it. Returns true when the harness fed
   * the message into the live TUI; false when there is no running turn to inject
   * into or the harness has no live terminal (SDK) — the caller then supersedes
   * via stop() + respawn.
   */
  injectIntoRunningTurn(content: string): boolean {
    return this.harness.injectIntoRunningTurn?.(content) ?? false;
  }

  /**
   * Tear down any parked/active CLI process the harness is keeping alive between
   * turns (PTY keep-alive). Called by SessionRunner on stop/shutdown so the
   * process never outlives the session and the Connected-TUI tab hides. No-op on
   * the SDK harness.
   */
  async dispose(): Promise<void> {
    await this.harness.dispose?.();
    // After the harness, so a final `sendEnded` still reaches attached viewers
    // before the in-pod server stops listening.
    await this.directStream?.dispose();
  }

  /**
   * Subscribe to "a human typed into the parked, idle TUI" (PTY keep-alive).
   * SessionRunner uses this to wake a passive turn. Returns unsubscribe; no-op
   * (returns a noop unsubscribe) on the SDK harness.
   */
  onPassiveActivity(handler: () => void): () => void {
    return this.harness.onPassiveActivity?.(handler) ?? (() => {});
  }

  /**
   * How the INITIAL instructions for this task would be delivered — "prefill"
   * means the TUI input is pre-filled but unsubmitted, awaiting the human.
   * Mirrors the decision runSdkQuery makes for the initial (non-follow-up)
   * query so SessionRunner can pick the matching state/timers.
   */
  initialPromptDelivery(context: TaskContext): "submit" | "prefill" {
    return resolvePromptDelivery({
      harnessKind: this.harnessKind,
      runnerMode: this.runnerConfig.mode,
      isAuto: this.mode.isAuto,
      agentMode: this.mode.effectiveMode,
      isFollowUp: false,
      hasExistingSession: hasExistingSessionFile(context.taskId, this.runnerConfig.workspaceDir, {
        agentMode: this.mode.effectiveMode,
        runnerMode: this.runnerConfig.mode,
      }),
    });
  }

  /**
   * Execute a Claude SDK query.
   * Without followUpContent: runs initial mode execution (build/plan).
   * With followUpContent: processes a follow-up user message.
   * `promptDelivery` overrides the executor's submit-vs-prefill resolution —
   * SessionRunner forces "submit" when pending chat messages already direct
   * the agent (prefilling would park them until a human touched the TUI).
   */
  async execute(
    context: TaskContext,
    followUpContent?: string | MultimodalBlock[],
    promptDelivery?: "submit" | "prefill",
  ): Promise<void> {
    this._stopped = false;
    this._wasRateLimited = false;
    this._wedgeAborted = false;
    this._abortController = new AbortController();
    const host = this.buildHost();
    try {
      await runSdkQuery(host, context, followUpContent, promptDelivery);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const isAbort = this._stopped || /abort/i.test(msg);
      if (isAbort) {
        logger.info("Query stopped by user", { error: msg });
      } else {
        logger.error("Query execution failed", { error: msg });
        this.connection.sendEvent({ type: "error", message: msg });
      }
    } finally {
      this.mode.pendingModeRestart = false;
      this._abortController = null;
    }
  }

  /**
   * Drive a passive turn: the parked CLI is producing transcript records because
   * a human typed into the idle Connected-TUI. Mirrors execute()'s setup (fresh
   * abort controller so a superseding chat message can abort, host construction,
   * error classification) but runs the promptless passive drain instead of a
   * full query.
   */
  async executePassive(context: TaskContext): Promise<void> {
    this._stopped = false;
    this._wasRateLimited = false;
    this._abortController = new AbortController();
    const host = this.buildHost();
    try {
      await runPassiveTurn(host, context);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const isAbort = this._stopped || /abort/i.test(msg);
      if (isAbort) {
        logger.info("Passive turn stopped", { error: msg });
      } else {
        logger.error("Passive turn failed", { error: msg });
        this.connection.sendEvent({ type: "error", message: msg });
      }
    } finally {
      this.mode.pendingModeRestart = false;
      this._abortController = null;
    }
  }

  // ── QueryHost construction ──────────────────────────────────────────

  private buildHost(): QueryHost {
    // oxlint-disable-next-line no-this-alias -- closure needed for live getter/setter delegation
    const bridge = this;
    return {
      config: this.runnerConfig,
      connection: this.connection,
      callbacks: this.callbacks,
      harness: this.harness,
      harnessKind: this.harnessKind,
      setupLog: [],
      sessionIds: this.sessionIds,
      pendingToolOutputs: this.pendingToolOutputs,

      // Live getters/setters delegating to ModeController + bridge state
      get agentMode() {
        return bridge.mode.effectiveMode;
      },
      get isAuto() {
        return bridge.mode.isAuto;
      },
      get isParentTask() {
        return bridge._isParentTask;
      },
      get hasExitedPlanMode() {
        return bridge.mode.hasExitedPlanMode;
      },
      set hasExitedPlanMode(val: boolean) {
        bridge.mode.hasExitedPlanMode = val;
      },
      get pendingModeRestart() {
        return bridge.mode.pendingModeRestart;
      },
      set pendingModeRestart(val: boolean) {
        bridge.mode.pendingModeRestart = val;
      },
      get discoveryCompleted() {
        return bridge._discoveryCompleted;
      },
      set discoveryCompleted(val: boolean) {
        bridge._discoveryCompleted = val;
      },
      get wasRateLimited() {
        return bridge._wasRateLimited;
      },
      set wasRateLimited(val: boolean) {
        bridge._wasRateLimited = val;
      },
      get wedgeAborted() {
        return bridge._wedgeAborted;
      },
      set wedgeAborted(val: boolean) {
        bridge._wedgeAborted = val;
      },
      get keyCycleCount() {
        return bridge._keyCycleCount;
      },
      set keyCycleCount(val: number) {
        bridge._keyCycleCount = val;
      },
      get activeQuery() {
        return bridge.activeQuery;
      },
      set activeQuery(val: AsyncGenerator<HarnessEvent, void> | null) {
        bridge.activeQuery = val;
      },
      get abortController() {
        return bridge._abortController;
      },

      isStopped: () => bridge._stopped,
      requestStop: () => bridge.stop(),
      requestSoftStop: () => {
        if (bridge.onSoftStop) bridge.onSoftStop();
      },
      createInputStream: (prompt) => bridge.createInputStream(prompt),
      onModeTransition: bridge.onModeTransition,
    };
  }

  // ── Input stream for Claude SDK ─────────────────────────────────────

  private async *createInputStream(
    prompt: string | MultimodalBlock[],
  ): AsyncGenerator<HarnessUserMessage, void, unknown> {
    yield {
      type: "user" as const,
      session_id: "",
      message: { role: "user" as const, content: prompt },
      parent_tool_use_id: null,
    };
  }
}
